"use client";

import { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { cn, Button } from "@wondermaps/ui";
import { findNearestPOIs } from "@wondermaps/maps/turf";
import type { POI } from "../MapCanvas";
import { analytics } from "../../lib/analytics";

/** Category quick action config */
interface QuickActionCategory {
  label: string;
  /** Category slugs to match (case-insensitive) */
  slugPatterns: string[];
}

/**
 * Quick action categories mapped to common POI category slugs.
 * Matches slugs containing these patterns (e.g., "restroom", "restrooms", "public-restroom").
 */
const QUICK_ACTIONS: (QuickActionCategory & { key: string })[] = [
  { label: "", key: "nearest.restroom", slugPatterns: ["restroom", "bathroom", "toilet"] },
  { label: "", key: "nearest.food", slugPatterns: ["food", "restaurant", "dining", "cafe", "eatery"] },
  { label: "", key: "nearest.exit", slugPatterns: ["exit", "entrance", "gate"] },
];

export interface NearestQuickActionsProps {
  pois: POI[];
  userLocation: { lng: number; lat: number } | null;
  onSelectPOI: (poi: POI) => void;
}

/**
 * Horizontal scrollable row of chip buttons for quick access to nearest amenities.
 * Hides chips for categories with no POIs.
 */
export function NearestQuickActions({
  pois,
  userLocation,
  onSelectPOI,
}: NearestQuickActionsProps) {
  const { t } = useTranslation("map");
  const handleQuickAction = useCallback(
    (key: string, poi: POI) => {
      // Extract category from key (e.g., "nearest.restroom" -> "restroom")
      const category = key.split(".")[1] ?? key;
      analytics.nearestQuickAction({ category });
      onSelectPOI(poi);
    },
    [onSelectPOI]
  );

  // Group POIs by quick action category and find nearest for each
  const nearestByCategory = useMemo(() => {
    if (!userLocation) return [];

    const results: Array<{ key: string; poi: POI }> = [];

    for (const action of QUICK_ACTIONS) {
      // Find POIs matching this category's slug patterns
      const matchingPois = pois.filter((poi) => {
        const slug = poi.categorySlug?.toLowerCase() ?? "";
        return action.slugPatterns.some((pattern) => slug.includes(pattern));
      });

      if (matchingPois.length === 0) continue;

      // Filter to POIs with valid locations
      const poisWithLocation = matchingPois.filter(
        (poi): poi is POI & { location: NonNullable<POI["location"]> } =>
          poi.location !== null
      );

      if (poisWithLocation.length === 0) continue;

      // Find nearest using turf helper
      const nearest = findNearestPOIs(
        { x: userLocation.lng, y: userLocation.lat },
        poisWithLocation.map((poi) => ({
          ...poi,
          location: { x: poi.location.coordinates[0], y: poi.location.coordinates[1] },
        })),
        1
      );

      if (nearest.length > 0) {
        // Find original POI by id (nearest returns modified object)
        const originalPoi = pois.find((p) => p.id === nearest[0].id);
        if (originalPoi) {
          results.push({ key: action.key, poi: originalPoi });
        }
      }
    }

    return results;
  }, [pois, userLocation]);

  // Don't render if no location or no matching categories
  if (!userLocation || nearestByCategory.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        // Horizontal scroll with hidden scrollbar
        "flex gap-2 overflow-x-auto py-2 px-4",
        // Hide scrollbar across browsers
        "scrollbar-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none]"
      )}
    >
      {nearestByCategory.map(({ key, poi }) => (
        <Button
          key={key}
          variant="outline"
          size="sm"
          className={cn(
            "shrink-0 h-9 px-4 rounded-full",
            "text-sm font-medium whitespace-nowrap",
            "bg-primary/10 border-primary/20 hover:bg-primary/20"
          )}
          onClick={() => handleQuickAction(key, poi)}
        >
          {t(key)}
        </Button>
      ))}
    </div>
  );
}
